import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { BookOpen, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Shared benefits list - used across all upgrade modals
 * Medical-grade tone: calm, supportive, non-salesy
 */
const FULL_ACCESS_BENEFITS = [
  'Unlimited blood & nutrition tracking',
  'Full symptom tracking (beyond 6)',
  'Integrative Health guidance',
  'Advanced trends & insights',
  'Doctor-ready PDF exports',
  'Export history & saved ranges',
  'Ongoing education updates'
];

/**
 * WhatsIncludedInline - Expandable benefits list for gate modals
 * Medical-grade tone: calm, supportive, non-salesy
 */
const WhatsIncludedInline = ({ expanded, onToggle, compact = false }) => {
  const benefits = FULL_ACCESS_BENEFITS;

  return (
    <div className="mt-3">
      <button
        onClick={onToggle}
        className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500 hover:text-teal-600 transition-colors flex items-center gap-1 mx-auto`}
      >
        See what&apos;s included
        {expanded ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>
      
      {expanded && (
        <div className="mt-4 pt-4 border-t border-slate-100 text-left">
          <p className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-slate-700 mb-3`}>
            Full access includes:
          </p>
          <ul className="space-y-2">
            {benefits.map((benefit, idx) => (
              <li key={idx} className={`flex items-start gap-2 ${compact ? 'text-xs' : 'text-sm'} text-slate-600`}>
                <Check className={`${compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} text-teal-500 mt-0.5 flex-shrink-0`} />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * ContentGateWrapper - Interaction-based content gating for Free plan users
 * 
 * TWO MODES:
 * 1. Page-level (mode="page") - Gates entire page content immediately (legacy)
 * 2. Content-level (mode="content") - Shows preview, gates on interaction (default)
 * 
 * Content-level gating is "curiosity-driven":
 * - Users can see titles, summaries, and previews
 * - Blur only activates when they try to access full content
 * - This lets users understand the value before hitting the gate
 * 
 * Props:
 * - children: Content to wrap
 * - mode: "page" | "content" (default: "content")
 * - preview: React node to show before gate (for content mode)
 * - onAttemptAccess: Callback when gated user tries to access content
 */
export const ContentGateWrapper = ({ 
  children, 
  mode = 'content',
  preview = null,
  className = '' 
}) => {
  const { user } = useAuth();
  const [attemptedAccess, setAttemptedAccess] = useState(false);
  
  // Bypass for admin or paid users
  const isAdmin = user?.role === 'admin';
  const isPaid = user?.plan === 'paid';
  const hasAccess = isAdmin || isPaid;

  // If user has access, show content directly
  if (hasAccess) {
    return <div className={className}>{children}</div>;
  }

  // Page-level gating - immediate blur (legacy mode)
  if (mode === 'page') {
    return (
      <div className={`relative ${className}`}>
        <div className="blur-sm opacity-50 pointer-events-none select-none" aria-hidden="true">
          {children}
        </div>
        <GateOverlay />
      </div>
    );
  }

  // Content-level gating - curiosity-driven
  // Show preview until user attempts to access full content
  if (!attemptedAccess && preview) {
    return (
      <div className={className}>
        <div onClick={() => setAttemptedAccess(true)} className="cursor-pointer">
          {preview}
        </div>
      </div>
    );
  }

  // User attempted access - show blur with gate
  return (
    <div className={`relative ${className}`}>
      <div className="blur-sm opacity-50 pointer-events-none select-none" aria-hidden="true">
        {children}
      </div>
      <GateOverlay compact />
    </div>
  );
};

/**
 * GatedContent - Wrap expandable/detailed content with interaction-based gating
 * 
 * Use this inside accordions, article expanders, "Read more" sections.
 * Shows a teaser and gates full content for free users.
 * Uses portal-based modal to avoid clipping issues.
 * 
 * Props:
 * - children: Full content (gated for free users)
 * - teaser: Optional preview shown before attempting access
 * - isExpanded: Whether the content should be expanded
 * - onExpand: Callback when expansion is attempted
 */
export const GatedContent = ({ 
  children, 
  teaser = null,
  isExpanded = false,
  onExpand = () => {},
  className = '' 
}) => {
  const { user } = useAuth();
  // Track if user has dismissed the modal (takes precedence over isExpanded)
  const [dismissed, setDismissed] = useState(false);
  
  // Bypass for admin or paid users
  const isAdmin = user?.role === 'admin';
  const isPaid = user?.plan === 'paid';
  const hasAccess = isAdmin || isPaid;

  // Reset dismissed state when content collapses (user clicks different article)
  React.useEffect(() => {
    if (!isExpanded) {
      setDismissed(false);
    }
  }, [isExpanded]);

  // If user has access, show content normally
  if (hasAccess) {
    return isExpanded ? <div className={className}>{children}</div> : null;
  }

  // If not expanded, nothing to display
  if (!isExpanded) {
    return null;
  }

  // Handle close - dismiss the modal but keep blur visible
  const handleClose = () => {
    setDismissed(true);
  };

  // Show blurred content and modal (unless dismissed)
  return (
    <div className={`relative ${className}`}>
      {/* Blurred preview of content */}
      <div className="blur-sm opacity-40 pointer-events-none select-none max-h-48 overflow-hidden" aria-hidden="true">
        {teaser || children}
      </div>
      {/* Portal-based modal - only show if not dismissed */}
      <GateModal 
        isOpen={!dismissed} 
        onClose={handleClose}
        compact 
      />
    </div>
  );
};

/**
 * GateModal - Full-screen modal rendered via Portal to avoid clipping
 * 
 * Renders at document.body level with fixed positioning to ensure
 * the popup is never clipped by parent containers, accordions, or grids.
 * Supports ESC key to close.
 */
export const GateModal = ({ isOpen, onClose, title, compact = false }) => {
  const [showBenefits, setShowBenefits] = useState(false);
  
  // Handle ESC key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);
  
  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl border border-slate-200 text-center ${compact ? 'p-6 max-w-sm' : 'p-8 max-w-md'} w-full mx-4 max-h-[90vh] overflow-y-auto`}
        role="dialog"
        aria-modal="true"
      >
        {/* Close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        )}
        
        {/* Icon */}
        <div className={`${compact ? 'w-12 h-12 mb-4' : 'w-14 h-14 mb-5'} bg-teal-50 rounded-full flex items-center justify-center mx-auto`}>
          <BookOpen className={`${compact ? 'h-6 w-6' : 'h-7 w-7'} text-teal-600`} />
        </div>
        
        {/* Message */}
        <h3 className={`${compact ? 'text-lg' : 'text-xl'} font-semibold text-slate-900 mb-2`}>
          {title || 'Guided resources & education'}
        </h3>
        <p className={`text-slate-600 ${compact ? 'text-sm mb-5' : 'text-sm mb-6'} leading-relaxed`}>
          {compact 
            ? 'Unlock full access to explore this content.'
            : 'This section includes guided information, education, and resources. Unlock full access to explore these tools.'
          }
        </p>
        
        {/* Primary CTA */}
        <Link
          to="/upgrade"
          className={`inline-block w-full bg-teal-600 text-white font-medium ${compact ? 'py-3 text-sm' : 'py-3.5'} px-6 rounded-xl hover:bg-teal-700 transition-colors`}
        >
          Unlock full access
        </Link>
        
        {/* Inline expandable "What's included" */}
        <WhatsIncludedInline 
          expanded={showBenefits} 
          onToggle={() => setShowBenefits(!showBenefits)}
          compact={compact}
        />
      </div>
    </div>
  );

  // Render via portal to document.body
  return createPortal(modalContent, document.body);
};

/**
 * GateOverlay - Legacy inline overlay (kept for backwards compatibility)
 * For new implementations, prefer GateModal for better visibility
 */
const GateOverlay = ({ compact = false }) => (
  <div className={`absolute inset-0 flex items-center justify-center ${compact ? 'p-2' : 'p-4'}`}>
    <div className={`bg-white/98 backdrop-blur-sm rounded-2xl shadow-xl border border-slate-200 text-center ${compact ? 'p-5 max-w-sm' : 'p-8 max-w-md'} w-full`}>
      {/* Icon */}
      <div className={`${compact ? 'w-10 h-10 mb-3' : 'w-14 h-14 mb-5'} bg-teal-50 rounded-full flex items-center justify-center mx-auto`}>
        <BookOpen className={`${compact ? 'h-5 w-5' : 'h-7 w-7'} text-teal-600`} />
      </div>
      
      {/* Message */}
      <h3 className={`${compact ? 'text-base' : 'text-xl'} font-semibold text-slate-900 mb-2`}>
        Guided resources & education
      </h3>
      <p className={`text-slate-600 ${compact ? 'text-xs mb-4' : 'text-sm mb-6'} leading-relaxed`}>
        {compact 
          ? 'Unlock full access to explore this content.'
          : 'This section includes guided information, education, and resources. Unlock full access to explore these tools.'
        }
      </p>
      
      {/* Primary CTA */}
      <Link
        to="/upgrade"
        className={`inline-block w-full bg-teal-600 text-white font-medium ${compact ? 'py-2.5 text-sm' : 'py-3.5'} px-6 rounded-xl hover:bg-teal-700 transition-colors mb-2`}
      >
        Unlock full access
      </Link>
      
      {/* Secondary link */}
      <Link
        to="/pricing#features"
        className={`${compact ? 'text-xs' : 'text-sm'} text-slate-500 hover:text-teal-600 transition-colors`}
      >
        See what&apos;s included
      </Link>
    </div>
  </div>
);

/**
 * useContentGate - Hook to check if content should be gated
 * 
 * Returns:
 * - isGated: Boolean - whether content should be gated
 * - hasAccess: Boolean - whether user has full access
 */
export const useContentGate = () => {
  const { user } = useAuth();
  
  const isAdmin = user?.role === 'admin';
  const isPaid = user?.plan === 'paid';
  const hasAccess = isAdmin || isPaid;
  
  return {
    isGated: !hasAccess,
    hasAccess
  };
};

export default ContentGateWrapper;
